import { useMemo, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { useTours } from "@/hooks/useTours";
import { useArtists } from "@/hooks/useArtists";
import { useAuth } from "@/hooks/useAuth";
import ReviewCard from "@/components/ReviewCard";

import { Button } from "@/components/ui/button";

import { Tour } from "@/types";
import { isBlacklistedArtistName } from "@/utils/blacklist";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { Skeleton } from "@/components/ui/skeleton";
import { deduplicateTours } from "@/utils/tourNormalization";
import FeaturedToursCarousel from "@/components/FeaturedToursCarousel";
import { SectionHeading } from "@/components/ui/section-heading";
import EmailVerificationReminder from "@/components/EmailVerificationReminder";
import HomeQuickLinks from "@/components/home/HomeQuickLinks";
import { useHomeCircleReviews } from "@/hooks/useHomeCircleReviews";
import { useHomeUpcomingTourStats } from "@/hooks/useHomeUpcomingTourStats";

// Below-fold sections — lazy-loaded for staged hydration
const TrendingSection = lazy(() => import("@/components/TrendingSection"));
const AIRecommendationsCarousel = lazy(() => import("@/components/AIRecommendationsCarousel"));
const TrendingReactionsCarousel = lazy(() => import("@/components/TrendingReactionsCarousel"));

const RailFallback = () => (
  <div className="space-y-3">
    <Skeleton className="h-5 w-40" />
    <div className="flex gap-3 overflow-hidden">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-40 w-40 shrink-0 rounded-lg" />
      ))}
    </div>
  </div>
);

// The shuffled Home ordering is intentional product behaviour, but it should not
// reshuffle just because the route remounted. One seed per app session keeps the
// same randomised order for a tab revisit while still varying between sessions.
const SESSION_SEED = Math.floor(Math.random() * 2 ** 31);

const seededShuffle = <T,>(items: T[], salt: number): T[] => {
  const arr = [...items];
  let state = (SESSION_SEED ^ (salt * 2654435761)) >>> 0 || 1;
  const next = () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    state >>>= 0;
    return state / 0xffffffff;
  };
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(next() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

// Editorial section header: shared eyebrow + display title primitive
const SectionHeader = SectionHeading;

const Index = () => {
  const { tours, loading: toursLoading } = useTours(true, 200);
  const { artists } = useArtists({ limit: 300 });
  const { user, loading } = useAuth();

  const {
    data: circle,
    isLoading: circleLoading,
  } = useHomeCircleReviews(user?.id);

  // O(1) lookup instead of a linear scan per tour — same result, less work per pass
  const artistNameById = useMemo(() => {
    const map = new Map<string, string | undefined>();
    for (const a of artists) map.set(a.id, a.name);
    return map;
  }, [artists]);

  const isTourBlacklisted = useMemo(
    () => (tour: Tour) => isBlacklistedArtistName(artistNameById.get(tour.artistId)),
    [artistNameById]
  );

  // Derived purely from cached tours — computed synchronously so a Home remount
  // with warm cache renders the same content with no intermediate empty frame.
  const trendingTours = useMemo(() => {
    if (toursLoading || tours.length === 0) return [] as Tour[];
    const ratedTours = tours.filter((t) => (t.averageRating || 0) > 0 && !isTourBlacklisted(t));
    const sortedTours = [...(ratedTours.length ? ratedTours : tours.filter((t) => !isTourBlacklisted(t)))].sort(
      (a, b) => (b.averageRating || 0) - (a.averageRating || 0)
    );
    return seededShuffle(deduplicateTours(sortedTours).slice(0, 24), 1);
  }, [tours, toursLoading, isTourBlacklisted]);

  const tourIds = useMemo(() => tours.map((t) => t.id), [tours]);
  const { data: upcomingStats } = useHomeUpcomingTourStats(tourIds);

  const upcomingTours = useMemo(() => {
    if (!upcomingStats) return [] as Tour[];
    const matching = tours.filter((t) => {
      const s = upcomingStats[t.id];
      return s && s.count >= 2 && !isTourBlacklisted(t);
    });
    const sorted = [...matching].sort((a, b) => {
      const sa = upcomingStats[a.id]!;
      const sb = upcomingStats[b.id]!;
      if (sa.firstDate !== sb.firstDate) return sa.firstDate - sb.firstDate;
      if (sb.count !== sa.count) return sb.count - sa.count;
      return (b.averageRating || 0) - (a.averageRating || 0);
    });
    return seededShuffle(deduplicateTours(sorted).slice(0, 24), 2);
  }, [tours, upcomingStats, isTourBlacklisted]);

  // highlightedTours keeps Trending from duplicating the Featured Tours surface.
  const highlightedTours = useMemo(() => {
    if (toursLoading || tours.length === 0) return [] as Tour[];
    if (upcomingTours.length === 0 && trendingTours.length === 0) return [] as Tour[];

    const upcomingIds = new Set(upcomingTours.map((t) => t.id));
    const poolById = new Map<string, Tour>();
    for (const t of [...upcomingTours, ...trendingTours]) {
      if (isTourBlacklisted(t)) continue;
      poolById.set(t.id, t);
    }

    if (poolById.size < 6) {
      const remaining = tours
        .filter((t) => !isTourBlacklisted(t) && !poolById.has(t.id))
        .sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
      for (const t of remaining) {
        poolById.set(t.id, t);
        if (poolById.size >= 6) break;
      }
    }

    const ordered = Array.from(poolById.values()).sort((a, b) => {
      const aUp = upcomingIds.has(a.id) ? 1 : 0;
      const bUp = upcomingIds.has(b.id) ? 1 : 0;
      if (aUp !== bUp) return bUp - aUp;
      if ((a.averageRating || 0) !== (b.averageRating || 0)) return (b.averageRating || 0) - (a.averageRating || 0);
      if ((a.concertCount || 0) !== (b.concertCount || 0)) return (b.concertCount || 0) - (a.concertCount || 0);
      return (a.name || "").localeCompare(b.name || "");
    });

    return ordered.slice(0, 6);
  }, [tours, toursLoading, trendingTours, upcomingTours, isTourBlacklisted]);

  const featuredSection = (
    <section className="section-rise">
      <FeaturedToursCarousel />
    </section>
  );

  // While auth is unresolved we still reserve the circle slot, so Trending can
  // never briefly occupy the "From Your Circle" position on a cold load.
  const circlePending = loading || (!!user && circleLoading && !circle);
  const showCircle = circlePending || (!!circle && circle.followingCount > 0);

  const circleSection = showCircle ? (
    <section className="section-rise">
      <SectionHeader
        eyebrow="From Your Circle"
        title="Recent Reviews"
        to="/discover?tab=feed&filter=friends"
      />
      {circlePending ? (
        // Approximate the height of a compact ReviewCard so resolving reviews
        // fill the reserved slot instead of pushing Trending far down the page.
        <div className="space-y-0 divide-y divide-border/40">
          {[0, 1, 2].map((i) => (
            <div key={i} className="py-3.5 flex gap-3 min-h-[196px]">
              <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-1/3" />
                <Skeleton className="h-3 w-1/4" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
                <Skeleton className="h-3 w-2/5" />
                <Skeleton className="h-6 w-2/3 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ) : circle && circle.reviews.length > 0 ? (
        <div className="space-y-0 divide-y divide-border/40">
          {circle.reviews.slice(0, 3).map((review) => (
            <ReviewCard key={review.id} review={review as any} compact />
          ))}
        </div>
      ) : (
        <div className="min-h-[96px] flex items-center border-y border-border/40 py-5">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Your circle is quiet right now. New reviews will appear here.
          </p>
        </div>
      )}
    </section>
  ) : null;

  const communitySection = (
    <section className="section-rise">
      <ErrorBoundary>
        <Suspense fallback={<RailFallback />}>
          <TrendingReactionsCarousel />
        </Suspense>
      </ErrorBoundary>
    </section>
  );

  const forYouSection = (
    <section className="section-rise">
      <ErrorBoundary>
        <Suspense fallback={<RailFallback />}>
          <AIRecommendationsCarousel />
        </Suspense>
      </ErrorBoundary>
    </section>
  );

  const trendingSection = (
    <section className="section-rise">
      <Suspense fallback={<RailFallback />}>
        <TrendingSection excludeIds={highlightedTours.map((t) => t.id)} />
      </Suspense>
    </section>
  );

  // Auth-pending renders the signed-in shape so the section order never
  // reorders between the first paint and the resolved session.
  const signedInLayout = loading || !!user;

  return (
    <div className="w-full overflow-x-hidden bg-background">
      <EmailVerificationReminder />
      <div className="max-w-3xl mx-auto w-full px-4 pt-1 pb-2 space-y-10 sm:space-y-12">

        {signedInLayout ? (
          <>
            {/* Home = the user's own SoundCheck world */}
            <section className="space-y-4">
              <SectionHeader
                eyebrow="Your SoundCheck"
                title="What's happening"
                to="/discover"
                ctaLabel="Explore"
              />
              <HomeQuickLinks />
            </section>

            {circleSection}
            {communitySection}
            {user ? forYouSection : null}

            {/* Discovery surfaces stay available, but sit below your world
                and point into Discover for the full experience. */}
            {featuredSection}
            {trendingSection}
          </>
        ) : (
          <>
            {featuredSection}
            {communitySection}
            {trendingSection}
          </>
        )}

        {/* Guest CTA — minimal, only for logged-out users */}
        {!loading && !user && (
          <section className="text-center py-8 border-t border-border">
            <h3 className="text-xl font-semibold font-display mb-2">
              Every show, remembered.
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Track, rate, and share your live music experiences.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button asChild size="lg">
                <Link to="/signin">Get Started</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/discover">Explore Discover</Link>
              </Button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default Index;
